"use client";

import {
  CalendarDays,
  ChevronRight,
  CircleHelp,
  FileText,
  MapPin,
  NotebookText,
  Share2,
  ShieldCheck,
  Star,
} from "lucide-react";
import { useRouter } from "next/navigation";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getExchangeRate } from "@/actions/forex";
import type { PackForSaleRow } from "@/actions/packs";
import type { ReviewListResponse } from "@/actions/review";
import type { EntityStoryStatus } from "@/actions/story";
import type {
  getPublicVenueDetail,
  PublicVenueExperience,
} from "@/actions/venue-public";
import {
  ContactDetailsDrawer,
  ContactDetailsRow,
} from "@/components/contact-details-sheet";
import { VenueLocationDrawer } from "@/components/maps/venue-location-drawer.client";
import { PricingDisclosure } from "@/components/pricing/pricing-breakdown";
import { SendInChatButton } from "@/components/share/send-in-chat-button";
import {
  DETAIL_DESKTOP,
  detailMediaAspectStyle,
} from "@/components/shell/detail-page-desktop";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import { BackButton } from "@/components/ui/back-button";
import { BannerCarousel } from "@/components/ui/banner-carousel";
import { Button } from "@/components/ui/button";
import {
  DETAIL_INFO_GROUP_ROW,
  DetailInfoGroup,
  DetailInfoRow,
} from "@/components/ui/detail-info-row";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import type { MediaData, SimpleMediaData } from "@/db/schema/hiive";
import { HostedByCard } from "@/features/hiives/components/hosted-by-card";
import { PackSaleCard } from "@/features/packs/components/pack-sale-card";
import { RefundPolicyDisclosure } from "@/features/refunds/components/refund-policy-acknowledgement";
import { EntityStoryRing } from "@/features/stories/components/entity-story-ring/entity-story-ring.client";
import { StoryHighlightsRow } from "@/features/stories/components/highlights/story-highlights-row.client";
import { useBackAwarePush } from "@/hooks/use-back-aware-push";
import { useBackNavigation } from "@/hooks/use-back-navigation";
import { useResolvedBackHref } from "@/hooks/use-current-href";
import { useIsDesktop, useWindowScrolledPast } from "@/hooks/use-is-desktop";
import { captureClientEvent } from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { ASPECT_RATIO_CLASS, ASPECT_RATIO_VALUE } from "@/lib/aspect-ratios";
import { formatAbsolute, formatAbsoluteTime } from "@/lib/dates/relative-time";
import { goBackTo } from "@/lib/go-back";
import { getIconComponent, type IconType } from "@/lib/icon-registry";
import { formatLocationDistance } from "@/lib/locations/nearby-location";
import { firstMediaPreviewUrl, type MediaAsset } from "@/lib/media";
import { cn } from "@/lib/utils";
import { venueActivityHref } from "@/lib/venue-links";
import { useExperienceNextSlots } from "./experience-slot-chips.client";
import { VenueFooterShell } from "./venue-footer-shell";

type VenueDetailData = Extract<
  Awaited<ReturnType<typeof getPublicVenueDetail>>,
  { ok: true }
>["data"];

type Props = {
  hiiveSlug: string;
  detail: VenueDetailData;
  distanceKm: number | null;
  reviewPreview: ReviewListResponse | null;
  storyRingStatus: EntityStoryStatus | null;
  /** The hiive's packs for sale — empty when it sells none. */
  packs: PackForSaleRow[];
};

const formatDistance = (km: number) => `${formatLocationDistance(km)} away`;

const formatCurrency = (amountMinor: number, currency: string) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    maximumFractionDigits: 0,
  }).format(amountMinor / 100);
const SETTLEMENT_CURRENCY = "INR";
type RatePreview = { rate: number; date: string; fetchedAtIso: string };

const policyRows = [
  {
    key: "terms",
    title: "Terms and conditions",
    icon: FileText,
  },
  {
    key: "faqs",
    title: "Frequently asked questions",
    icon: CircleHelp,
  },
] as const;

export function VenueExperiencesPageClient({
  hiiveSlug,
  detail,
  distanceKm,
  reviewPreview,
  storyRingStatus,
  packs,
}: Props) {
  const router = useRouter();
  // Into a class: its back arrow must return here WITH this page's own
  // `?from=`, or the venue's next back loses where the reader came from.
  const pushWithFrom = useBackAwarePush();
  const backHref = useResolvedBackHref("/hiives");
  const { venue, experiences, hostedEvents } = detail;
  useEffect(() => {
    captureClientEvent(ANALYTICS_EVENTS.VENUE_VIEWED, {
      venue_id: detail.venue.id,
      hiive_slug: hiiveSlug,
    });
  }, [detail.venue.id, hiiveSlug]);
  // Desktop: the shared two-column detail layout. The banner and the book
  // button move into a sticky left column; the phone keeps the scrolling
  // column with a floating footer.
  const isDesktop = useIsDesktop();
  const showBarTitle = useWindowScrolledPast(120, isDesktop);
  const [activityDrawerOpen, setActivityDrawerOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const venueContactDetails = useMemo(
    () => ({
      contactPhone: venue.contactPhone,
      contactEmail: venue.contactEmail,
      websiteUrl: venue.websiteUrl,
      socialLinks: venue.socialLinks,
    }),
    [
      venue.contactPhone,
      venue.contactEmail,
      venue.websiteUrl,
      venue.socialLinks,
    ],
  );
  const [drawer, setDrawer] = useState<
    "terms" | "faqs" | "house-rules" | "booking-notes" | null
  >(null);

  // See `goBackTo`: a bare `router.push` here lost the scroll position of
  // whatever list sent the reader to this venue.
  const handleBrowserBack = useCallback(() => {
    goBackTo(router, backHref);
  }, [backHref, router]);

  useBackNavigation({
    behavior: "intercept",
    onBack: handleBrowserBack,
    cleanupOnUnmount: false,
  });

  const galleryMedia = useMemo(() => {
    const rawMedia = (
      Array.isArray(venue.bannerMedia) ? venue.bannerMedia : []
    ) as MediaAsset[];

    // De-duplicate by key or URL
    const seenKeys = new Set<string>();
    const uniqueMedia = rawMedia.filter((mediaItem) => {
      const item = mediaItem as Record<string, unknown>;
      const identifier =
        (item.key as string | undefined) || (item.url as string | undefined);
      if (!identifier) return false;
      if (seenKeys.has(identifier)) return false;
      seenKeys.add(identifier);
      return true;
    });

    return (
      uniqueMedia.length > 0
        ? uniqueMedia
        : [
            {
              id: "placeholder",
              key: "images/movement-hero.avif",
              order: 0,
              url: "/images/movement-hero.avif",
            },
          ]
    ) as MediaData[];
  }, [venue.bannerMedia]);

  const activities = useMemo(() => {
    const map = new Map<
      string,
      {
        activityId: string;
        activityName: string;
        activityIconKey: string;
        experiences: PublicVenueExperience[];
        unitCount: number;
        unitLabel: string;
      }
    >();

    for (const experience of experiences) {
      const existing = map.get(experience.activityId);
      if (existing) {
        existing.experiences.push(experience);
        existing.unitCount += experience.availableUnitCount;
        continue;
      }
      map.set(experience.activityId, {
        activityId: experience.activityId,
        activityName: experience.activityName,
        activityIconKey: experience.activityIconKey,
        experiences: [experience],
        unitCount: experience.availableUnitCount,
        unitLabel: experience.unitLabel,
      });
    }

    return Array.from(map.values());
  }, [experiences]);
  const [ratePreviewByCurrency, setRatePreviewByCurrency] = useState<
    Record<string, RatePreview>
  >({});

  const previewCurrencies = useMemo(
    () =>
      Array.from(
        new Set(
          experiences
            .map((experience) => experience.currency.toUpperCase())
            .filter((currency) => currency !== SETTLEMENT_CURRENCY),
        ),
      ),
    [experiences],
  );

  useEffect(() => {
    if (previewCurrencies.length === 0) return;
    let canceled = false;

    for (const currency of previewCurrencies) {
      if (ratePreviewByCurrency[currency]) continue;
      getExchangeRate({ from: currency }).then((result) => {
        if (canceled || !result.ok) return;
        setRatePreviewByCurrency((current) => ({
          ...current,
          [currency]: {
            rate: result.data.rate.rate,
            date: result.data.rate.date,
            fetchedAtIso: result.data.rate.fetchedAtIso,
          },
        }));
      });
    }

    return () => {
      canceled = true;
    };
  }, [previewCurrencies, ratePreviewByCurrency]);

  const ctaLabel = "Book slots";
  const reviewHref = `/hiives/${hiiveSlug}/venues/${venue.slug}/reviews`;
  const hasMore =
    Boolean(venue.refundPolicy) ||
    venue.termsAndConditions.length > 0 ||
    venue.faqs.length > 0;

  return (
    <main className="min-h-dvh bg-background pb-[calc(env(safe-area-inset-bottom)+8rem)] text-text-primary dark:bg-[#0b0b0f] dark:text-white wide:pb-0">
      <div
        className={cn(
          "pointer-events-none fixed inset-x-0 top-0 z-1001 border-b border-transparent",
          DETAIL_DESKTOP.barSurface,
          DETAIL_DESKTOP.barColumn,
        )}
      >
        <div
          className={cn(
            "mx-auto flex max-w-[640px] items-center justify-between gap-3 px-4 pt-[calc(env(safe-area-inset-top)+12px)] wide:pt-0",
            DETAIL_DESKTOP.barInner,
            DETAIL_DESKTOP.barColumnRow,
            DETAIL_DESKTOP.barCorner,
          )}
        >
          <BackButton
            fallbackHref="/hiives"
            label="Back"
            className={cn(
              "pointer-events-auto h-11 w-11 rounded-full bg-black/45 text-white shadow-lg backdrop-blur hover:bg-black/60 wide:h-10 wide:w-10",
              DETAIL_DESKTOP.barButton,
              DETAIL_DESKTOP.barColumnButton,
            )}
          />
          {/* Desktop only, like the hiive and event bars: the name joins the
              bar once the heading in the right column has scrolled away. */}
          <span
            aria-hidden={!showBarTitle}
            className={cn(
              "hidden min-w-0 flex-1 truncate px-2 font-semibold text-text-primary opacity-0 transition-opacity duration-300 wide:block",
              DETAIL_DESKTOP.barTitle,
              showBarTitle && "opacity-100",
            )}
          >
            {venue.name}
          </span>
          <div className="flex items-center gap-2">
            <SendInChatButton
              entityType="venue"
              entityId={String(venue.id)}
              aria-label="Share venue"
              className={cn(
                "pointer-events-auto flex h-11 w-11 items-center justify-center rounded-full bg-black/45 text-white shadow-lg backdrop-blur hover:bg-black/60 wide:h-10 wide:w-10",
                DETAIL_DESKTOP.barButton,
                DETAIL_DESKTOP.barColumnButton,
              )}
              nativeShare={{
                title: venue.name,
                text: venue.address,
              }}
            >
              <Share2 className="size-7" />
            </SendInChatButton>
          </div>
        </div>
      </div>

      <div
        className={cn(
          "mx-auto min-h-dvh max-w-[640px] bg-background dark:bg-[#0b0b0f]",
          DETAIL_DESKTOP.grid,
        )}
      >
        <div
          className={DETAIL_DESKTOP.mediaColumn}
          style={detailMediaAspectStyle(
            galleryMedia,
            ASPECT_RATIO_VALUE.venueBooking,
          )}
        >
          <section className={cn("relative", DETAIL_DESKTOP.media)}>
            <BannerCarousel
              media={galleryMedia}
              altName={venue.name}
              aspectRatio={cn(
                ASPECT_RATIO_CLASS.venueBooking,
                DETAIL_DESKTOP.mediaAspect,
              )}
            />

            {storyRingStatus?.hasActive ? (
              <div className="absolute bottom-4 left-4 z-10">
                <EntityStoryRing
                  authorType="venue"
                  authorId={String(venue.id)}
                  name={venue.name}
                  avatarUrl={firstMediaPreviewUrl(galleryMedia)}
                  href={null}
                  status={storyRingStatus}
                  size={56}
                >
                  <Avatar className="h-full w-full rounded-full">
                    <AvatarImage
                      src={firstMediaPreviewUrl(galleryMedia) ?? undefined}
                    />
                    <AvatarFallback>
                      {venue.name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </EntityStoryRing>
              </div>
            ) : null}
          </section>
          {isDesktop && experiences.length > 0 ? (
            <VenueFooterShell variant="inline">
              <FooterBookSlotsButton
                hiiveSlug={hiiveSlug}
                venueSlug={venue.slug}
                experiences={experiences}
                activities={activities}
                label={ctaLabel}
                onOpenDrawer={() => setActivityDrawerOpen(true)}
              />
            </VenueFooterShell>
          ) : null}
        </div>

        <section
          className={cn(
            "space-y-8 px-4 pt-6 wide:px-0 wide:pt-0",
            DETAIL_DESKTOP.content,
          )}
        >
          <div className="space-y-4">
            {detail.preview ? (
              <div className="rounded-2xl border border-border bg-surface p-4 text-sm dark:border-white/[0.1] dark:bg-glass-bg-04">
                <p className="font-semibold">Preview for your team</p>
                <p className="mt-1 leading-relaxed text-text-secondary dark:text-white/62">
                  Members can't see this venue or its draft experiences yet.
                  Submit it from the venue dashboard when it looks right.
                </p>
              </div>
            ) : null}
            <h1 className="text-[28px] leading-tight font-semibold tracking-normal wide:text-3xl">
              {venue.name}
            </h1>

            <DetailInfoRow
              icon={MapPin}
              title={venue.name}
              subtitle={`${distanceKm != null ? `${formatDistance(distanceKm)} · ` : ""}${venue.address}`}
              onClick={() => setLocationOpen(true)}
            />

            <ContactDetailsRow
              details={venueContactDetails}
              subtitle="Get in touch with the venue"
              onOpen={() => setContactOpen(true)}
            />

            <StoryHighlightsRow ownerType="venue" ownerId={String(venue.id)} />

            <DetailInfoRow
              icon={Star}
              title="Reviews"
              subtitle={
                reviewPreview && reviewPreview.aggregate.publishedCount > 0
                  ? `★ ${reviewPreview.aggregate.averageRating.toFixed(1)} (${reviewPreview.aggregate.publishedCount} ${
                      reviewPreview.aggregate.publishedCount === 1
                        ? "review"
                        : "reviews"
                    }) · See reviews`
                  : "Be the first to review"
              }
              href={reviewHref}
            />

            {venue.description ? (
              <p className="text-sm leading-relaxed whitespace-pre-line text-text-secondary dark:text-white/62">
                {venue.description}
              </p>
            ) : null}
          </div>

          {/* The hiive's packs beside its classes — the same ticket card as
              the packs page, so one feature never reads as two. A pack only
              makes sense as a comparison with booking once, and this list is
              where once is being weighed. */}
          {packs.length > 0 ? (
            <section>
              <h2 className="mb-3 text-xl font-semibold text-text-primary">
                Save with a pack
              </h2>
              <div className="flex flex-col gap-3">
                {packs.map((pack) => (
                  <PackSaleCard
                    key={pack.packId}
                    pack={pack}
                    hiiveSlug={hiiveSlug}
                    hiiveName={venue.hiiveName}
                    hiiveLogoImage={
                      venue.hiiveLogoImage as SimpleMediaData | null
                    }
                  />
                ))}
              </div>
            </section>
          ) : null}

          {hostedEvents.length > 0 ? (
            <section className="space-y-3">
              <div>
                <p className="text-[12px] font-semibold tracking-[0.24em] text-text-tertiary uppercase dark:text-white/42">
                  Events at this venue
                </p>
                <p className="mt-1 text-sm text-text-secondary dark:text-white/58">
                  Times come directly from each event&apos;s schedule.
                </p>
              </div>
              <div className="space-y-3">
                {hostedEvents.map((hostedEvent) => (
                  <BackAwareLink
                    key={hostedEvent.id}
                    href={`/hiives/${hiiveSlug}/events/${hostedEvent.slug}`}
                    className="group block overflow-hidden rounded-2xl border border-border bg-surface p-4 shadow-sm transition hover:border-brand/35 hover:bg-surface-alt dark:border-glass-border-08 dark:bg-white/[0.055] dark:hover:bg-white/[0.08]"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand dark:bg-[#F5E6B8]/10 dark:text-[#F5E6B8]">
                        <CalendarDays className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <h2 className="text-base font-semibold text-text-primary dark:text-white">
                            {hostedEvent.name}
                          </h2>
                          <ChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-text-tertiary transition-transform group-hover:translate-x-0.5 dark:text-white/40" />
                        </div>
                        <div className="mt-2 flex [scrollbar-width:none] gap-2 overflow-x-auto pb-0.5">
                          {hostedEvent.dates.map((eventDate) => (
                            <span
                              key={eventDate.id}
                              className="shrink-0 rounded-xl border border-border bg-surface-alt px-3 py-2 text-center dark:border-glass-border-08 dark:bg-black/20"
                            >
                              <span className="block text-xs font-semibold text-text-primary dark:text-white">
                                {formatAbsolute(eventDate.startTime, {
                                  weekday: "short",
                                  day: "numeric",
                                  month: "short",
                                })}
                              </span>
                              <span className="mt-0.5 block text-xs text-text-secondary dark:text-white/58">
                                {formatAbsoluteTime(eventDate.startTime, {
                                  hour: "numeric",
                                  minute: "2-digit",
                                })}
                              </span>
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </BackAwareLink>
                ))}
              </div>
            </section>
          ) : null}

          {venue.amenities.length > 0 ? (
            <section className="space-y-3">
              <p className="text-[12px] font-semibold tracking-[0.24em] text-text-tertiary uppercase dark:text-white/42">
                Amenities available
              </p>
              <div className="grid grid-cols-2 gap-2">
                {venue.amenities.slice(0, 8).map((amenity) => (
                  <div
                    key={amenity}
                    className="flex min-h-11 items-center rounded-2xl border border-border bg-surface px-3 text-sm text-text-secondary shadow-sm dark:border-glass-border-08 dark:bg-white/[0.055] dark:text-white/72"
                  >
                    {amenity}
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {(venue.policy || venue.bookingNotes) && (
            <section className="space-y-3">
              <p className="text-[12px] font-semibold tracking-[0.24em] text-text-tertiary uppercase dark:text-white/42">
                Before you book
              </p>
              <DetailInfoGroup>
                {venue.policy ? (
                  <DetailInfoRow
                    icon={ShieldCheck}
                    size="sm"
                    title="House rules"
                    onClick={() => setDrawer("house-rules")}
                    className={DETAIL_INFO_GROUP_ROW}
                  />
                ) : null}
                {venue.bookingNotes ? (
                  <DetailInfoRow
                    icon={NotebookText}
                    size="sm"
                    title="Booking notes"
                    onClick={() => setDrawer("booking-notes")}
                    className={DETAIL_INFO_GROUP_ROW}
                  />
                ) : null}
              </DetailInfoGroup>
            </section>
          )}

          {hasMore ? (
            <section className="space-y-3">
              <p className="text-[12px] font-semibold tracking-[0.24em] text-text-tertiary uppercase dark:text-white/42">
                More
              </p>
              {venue.refundPolicy ? (
                <RefundPolicyDisclosure
                  policy={venue.refundPolicy}
                  hasCashPayment={false}
                />
              ) : null}

              <DetailInfoGroup>
                {policyRows.map((row) => {
                  const isAvailable =
                    row.key === "terms"
                      ? venue.termsAndConditions.length > 0
                      : venue.faqs.length > 0;
                  if (!isAvailable) return null;
                  return (
                    <DetailInfoRow
                      key={row.key}
                      icon={row.icon}
                      size="sm"
                      title={row.title}
                      onClick={() => setDrawer(row.key)}
                      className={DETAIL_INFO_GROUP_ROW}
                    />
                  );
                })}
              </DetailInfoGroup>
            </section>
          ) : null}

          <HostedByCard
            hiive={{
              name: venue.hiiveName,
              logoImage: venue.hiiveLogoImage as MediaAsset | null,
              slug: hiiveSlug,
            }}
            eventCount={venue.hiiveEventCount}
            memberCount={venue.hiiveMemberCount}
            className="pb-6"
          />
        </section>
      </div>

      {!isDesktop && experiences.length > 0 ? (
        <VenueFooterShell>
          <FooterBookSlotsButton
            hiiveSlug={hiiveSlug}
            venueSlug={venue.slug}
            experiences={experiences}
            activities={activities}
            label={ctaLabel}
            onOpenDrawer={() => setActivityDrawerOpen(true)}
          />
        </VenueFooterShell>
      ) : null}

      <Drawer open={activityDrawerOpen} onOpenChange={setActivityDrawerOpen}>
        <DrawerContent className="mx-auto max-h-[82vh] max-w-[640px] rounded-t-[28px] border-border bg-surface text-text-primary shadow-xl backdrop-blur-2xl dark:border-white/[0.12] dark:bg-[#0b0b0f]/85 dark:text-white dark:shadow-2xl">
          <DrawerHeader className="flex flex-row items-center justify-between border-b border-border px-5 py-4 text-left dark:border-white/[0.1]">
            <DrawerTitle className="text-2xl font-semibold text-text-primary dark:text-white">
              Select activity
            </DrawerTitle>
          </DrawerHeader>
          <div className="grid gap-3 overflow-y-auto p-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] sm:grid-cols-2">
            {activities.map((activity) => {
              const ActivityIcon = getIconComponent(
                activity.activityIconKey as IconType,
              );
              const currency =
                activity.experiences[0]?.currency ?? venue.currency;
              const startingPrice = Math.min(
                ...activity.experiences.map(
                  (experience) => experience.basePriceMinor,
                ),
              );
              // "From" only when the activity's experiences differ in price.
              // Six yoga classes at one price are ₹1,500, not "from" it.
              const priceVaries = activity.experiences.some(
                (experience) => experience.basePriceMinor !== startingPrice,
              );
              const ratePreview = ratePreviewByCurrency[currency.toUpperCase()];
              const settlementPrice =
                currency.toUpperCase() !== SETTLEMENT_CURRENCY && ratePreview
                  ? Math.round(startingPrice * ratePreview.rate)
                  : null;
              return (
                <button
                  key={activity.activityId}
                  type="button"
                  className="flex items-center gap-3 rounded-[18px] border border-border bg-surface-alt p-3 text-left shadow-sm backdrop-blur-md transition duration-300 hover:bg-surface-elevated dark:border-glass-border-06 dark:bg-glass-bg-02 dark:hover:bg-glass-bg-06"
                  onClick={() => {
                    setActivityDrawerOpen(false);
                    // The activity, not its first class: the member picked
                    // "Yoga" and chooses which yoga on the next screen, with
                    // the first class already picked.
                    pushWithFrom(
                      activityHref(hiiveSlug, venue.slug, activity.activityId),
                    );
                  }}
                >
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] border border-border bg-surface text-text-secondary dark:border-glass-border-08 dark:bg-glass-bg-04 dark:text-white/80">
                    <ActivityIcon className="h-5 w-5" color="currentColor" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-lg leading-tight font-semibold text-text-primary dark:text-white">
                      {activity.activityName}
                    </div>
                    <div className="mt-1 text-sm text-text-secondary dark:text-white/58">
                      {priceVaries ? "From " : null}
                      <span className="font-medium text-text-primary dark:text-white">
                        {settlementPrice != null
                          ? formatCurrency(settlementPrice, SETTLEMENT_CURRENCY)
                          : formatCurrency(startingPrice, currency)}
                      </span>
                      <PricingDisclosure
                        policy={
                          activity.experiences.find(
                            (experience) =>
                              experience.basePriceMinor === startingPrice,
                          )?.pricingPolicy
                        }
                      />
                      {settlementPrice != null ? (
                        <span className="ml-1 text-text-tertiary dark:text-white/42">
                          listed as {formatCurrency(startingPrice, currency)}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 shrink-0 text-text-tertiary dark:text-white/40" />
                </button>
              );
            })}
          </div>
        </DrawerContent>
      </Drawer>

      <VenueLocationDrawer
        open={locationOpen}
        onOpenChange={setLocationOpen}
        venue={{
          name: venue.name,
          address: venue.address,
          latitude: venue.latitude,
          longitude: venue.longitude,
        }}
      />

      <ContactDetailsDrawer
        open={contactOpen}
        onOpenChange={setContactOpen}
        details={venueContactDetails}
        subjectLabel="venue"
      />

      <PolicyDrawer
        open={drawer !== null}
        onOpenChange={(open) => {
          if (!open) setDrawer(null);
        }}
        title={
          drawer === "terms"
            ? "Terms and conditions"
            : drawer === "faqs"
              ? "Frequently asked questions"
              : drawer === "house-rules"
                ? "House rules"
                : "Booking notes"
        }
      >
        {drawer === "terms" ? (
          <StructuredItems items={venue.termsAndConditions} />
        ) : drawer === "faqs" ? (
          <StructuredItems items={venue.faqs} />
        ) : drawer === "house-rules" ? (
          <p className="text-sm leading-relaxed whitespace-pre-wrap text-text-secondary dark:text-white/68">
            {venue.policy}
          </p>
        ) : (
          <p className="text-sm leading-relaxed whitespace-pre-wrap text-text-secondary dark:text-white/68">
            {venue.bookingNotes}
          </p>
        )}
      </PolicyDrawer>
    </main>
  );
}

const activityHref = (
  hiiveSlug: string,
  venueSlug: string,
  activityId: string,
  startIso?: string,
) => venueActivityHref({ hiiveSlug, venueSlug, activityId, start: startIso });

const FOOTER_CTA_CLASSNAME =
  "h-auto w-full rounded-full bg-brand px-5 py-3 text-center font-semibold text-brand-foreground text-sm transition-transform hover:scale-[1.01] hover:bg-brand-hover focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:px-6 sm:text-base dark:bg-white dark:text-black dark:hover:bg-white/92";

/**
 * The footer's "Book slots".
 *
 * One experience — or one activity, which is the same choice one level up —
 * means the activity drawer would ask a question with only one answer, so it
 * is skipped: the tap goes straight to that experience's booking page with
 * its next bookable start preselected (`?start=`), exactly as if the member
 * had tapped its first slot chip. Anything with a real choice still gets the
 * drawer. Every path navigates; none scroll in place.
 */
function FooterBookSlotsButton({
  hiiveSlug,
  venueSlug,
  experiences,
  activities,
  label,
  onOpenDrawer,
}: {
  hiiveSlug: string;
  venueSlug: string;
  experiences: PublicVenueExperience[];
  activities: Array<{
    activityId: string;
    experiences: PublicVenueExperience[];
  }>;
  label: string;
  onOpenDrawer: () => void;
}) {
  const autoExperience =
    experiences.length === 1
      ? (experiences[0] ?? null)
      : activities.length === 1
        ? (activities[0]?.experiences[0] ?? null)
        : null;
  // One activity with several classes still has a choice to make — which
  // class — so it goes to the activity page rather than straight to a class.
  const autoActivityId =
    experiences.length > 1 && activities.length === 1
      ? (activities[0]?.activityId ?? null)
      : null;

  if (!autoExperience) {
    return (
      <Button
        type="button"
        className={FOOTER_CTA_CLASSNAME}
        onClick={onOpenDrawer}
      >
        {label}
      </Button>
    );
  }

  return (
    <AutoBookSlotsButton
      hiiveSlug={hiiveSlug}
      venueSlug={venueSlug}
      experience={autoExperience}
      activityId={autoActivityId}
      label={label}
    />
  );
}

function AutoBookSlotsButton({
  hiiveSlug,
  venueSlug,
  experience,
  activityId,
  label,
}: {
  hiiveSlug: string;
  venueSlug: string;
  experience: PublicVenueExperience;
  /** Set when the tap should open the activity page on this class. */
  activityId: string | null;
  label: string;
}) {
  const pushWithFrom = useBackAwarePush();
  // The footer CTA is the one place a start is still preselected: with a single
  // experience there is no list to choose from, so the next bookable slot is
  // the only thing the tap could mean.
  const { data: nextSlots } = useExperienceNextSlots(experience.id);

  return (
    <Button
      type="button"
      className={FOOTER_CTA_CLASSNAME}
      onClick={() => {
        const first = nextSlots?.[0];
        if (activityId) {
          pushWithFrom(
            activityHref(hiiveSlug, venueSlug, activityId, first?.startIso),
          );
          return;
        }
        pushWithFrom(
          venueActivityHref({
            hiiveSlug,
            venueSlug,
            activityId: experience.activityId,
            experienceSlug: experience.slug,
            start: first?.startIso,
          }),
        );
      }}
    >
      {label}
    </Button>
  );
}

function StructuredItems({
  items,
}: {
  items: Array<{ title: string; description?: string | null }>;
}) {
  return (
    <div className="divide-y divide-border dark:divide-white/[0.08]">
      {items.map((item) => (
        <div key={`${item.title}-${item.description ?? ""}`} className="py-4">
          <p className="text-sm font-semibold text-text-primary dark:text-white">
            {item.title}
          </p>
          {item.description ? (
            <p className="mt-1.5 text-sm leading-relaxed whitespace-pre-wrap text-text-secondary dark:text-white/58">
              {item.description}
            </p>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function PolicyDrawer({
  open,
  onOpenChange,
  title,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: ReactNode;
}) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="mx-auto max-h-[82vh] max-w-[640px] rounded-t-[28px] border-border bg-surface text-text-primary shadow-xl dark:border-white/[0.12] dark:bg-[#101116] dark:text-white">
        <DrawerHeader className="flex flex-row items-center justify-between border-b border-border px-5 py-4 text-left dark:border-white/[0.1]">
          <DrawerTitle className="text-xl font-semibold text-text-primary dark:text-white">
            {title}
          </DrawerTitle>
        </DrawerHeader>
        <div className="overflow-y-auto p-5 pb-[max(env(safe-area-inset-bottom),1.25rem)]">
          {children}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
